import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/authContext';
import { useFirebase } from '../context/firebaseContext';
import {
    updateProfile,
    updateEmail,
    EmailAuthProvider,
    reauthenticateWithCredential,
    updatePassword,
} from 'firebase/auth';
import { doc, getDoc, setDoc, updateDoc } from 'firebase/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLock, faCheck, faTimes, faChevronDown, faChevronUp } from '@fortawesome/free-solid-svg-icons';

const ProfileComponent = () => {
    const { user, loading } = useAuth();
    const { db } = useFirebase();
    
    // Current values from database
    const [currentUsername, setCurrentUsername] = useState('');
    const [currentEmail, setCurrentEmail] = useState('');
    
    // New values for updates
    const [newUsername, setNewUsername] = useState('');
    const [confirmUsername, setConfirmUsername] = useState('');
    const [newEmail, setNewEmail] = useState('');
    const [confirmEmail, setConfirmEmail] = useState('');
    
    // Password fields
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    
    // UI state
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [accountType, setAccountType] = useState('');
    const [fetching, setFetching] = useState(true);
    const [validationErrors, setValidationErrors] = useState({});
    const [updateLoading, setUpdateLoading] = useState({
        username: false,
        email: false,
        password: false
    });
    
    // Dropdown states
    const [expandedSections, setExpandedSections] = useState({
        username: false,
        email: false,
        password: false
    });
    
    // Fetch user data on mount
    useEffect(() => {
        const fetchUserData = async () => {
            if (user) {
                setFetching(true);
                const userDocRef = doc(db, 'users', user.uid);
                try {
                    const docSnapshot = await getDoc(userDocRef);
                    if (docSnapshot.exists()) {
                        const userData = docSnapshot.data() || {};
                        setCurrentUsername(userData.username || '');
                        setCurrentEmail(userData.email || user.email || '');
                        setAccountType(userData.accountType || '');
                    } else {
                        setCurrentEmail(user.email || '');
                    }
                } catch (err) {
                    setError('Error fetching user data');
                } finally {
                    setFetching(false);
                }
            } else {
                setFetching(false);
            }
        };
        fetchUserData();
    }, [user, db]);

    const isGold = accountType === 'gold';

    // Validation functions
    const validateUsername = useCallback(() => {
        if (!newUsername) return null;
        if (newUsername.length < 3) return 'Username must be at least 3 characters';
        if (newUsername !== confirmUsername) return 'New username and confirmation do not match';
        return null;
    }, [newUsername, confirmUsername]);

    const validateEmail = useCallback(() => {
        if (!newEmail) return null;
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(newEmail)) return 'Please enter a valid email address';
        if (newEmail !== confirmEmail) return 'Emails do not match';
        return null;
    }, [newEmail, confirmEmail]);

    const validatePassword = useCallback(() => {
        if (!newPassword) return null;
        if (newPassword.length < 6) return 'Password must be at least 6 characters';
        if (newPassword !== confirmNewPassword) return 'Passwords do not match';
        return null;
    }, [newPassword, confirmNewPassword]);

    // Update validation errors when fields change
    useEffect(() => {
        const errors = {};
        const usernameError = validateUsername();
        const emailError = validateEmail();
        const passwordError = validatePassword();
        
        if (usernameError) errors.username = usernameError;
        if (emailError) errors.email = emailError;
        if (passwordError) errors.password = passwordError;
        
        setValidationErrors(errors);
    }, [validateUsername, validateEmail, validatePassword]);

    const toggleSection = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const handleUpdateUsername = async () => {
        if (!newUsername || !confirmUsername || newUsername !== confirmUsername) {
            setError('Please fill in both username fields correctly.');
            return;
        }

        setUpdateLoading(prev => ({ ...prev, username: true }));
        setError('');
        setSuccess('');

        try {
            if (user) {
                // Verify gold membership again
                const userDocRef = doc(db, 'users', user.uid);
                const docSnapshot = await getDoc(userDocRef);
                const userData = docSnapshot.data() || {};
                
                if (userData.accountType !== 'gold') {
                    setError('Only Gold members can change their username.');
                    return;
                }

                // Update Firestore
                await updateDoc(userDocRef, { username: newUsername });
                
                // Update Firebase Auth profile
                await updateProfile(user, { displayName: newUsername });

                setCurrentUsername(newUsername);
                setNewUsername('');
                setConfirmUsername('');
                setSuccess('Username updated successfully.');
            }
        } catch (error) {
            setError('Failed to update username: ' + error.message);
        } finally {
            setUpdateLoading(prev => ({ ...prev, username: false }));
        }
    };

    const handleUpdateEmail = async () => {
        if (!newEmail || !confirmEmail || newEmail !== confirmEmail) {
            setError('Please fill in both email fields correctly.');
            return;
        }

        setUpdateLoading(prev => ({ ...prev, email: true }));
        setError('');
        setSuccess('');

        try {
            if (user) {
                await updateEmail(user, newEmail);
                
                // Update Firestore
                const userDocRef = doc(db, 'users', user.uid);
                await updateDoc(userDocRef, { email: newEmail });

                setCurrentEmail(newEmail);
                setNewEmail('');
                setConfirmEmail('');
                setSuccess('Email updated successfully.');
            }
        } catch (error) {
            setError('Failed to update email: ' + error.message);
        } finally {
            setUpdateLoading(prev => ({ ...prev, email: false }));
        }
    };

    const handleUpdatePassword = async () => {
        if (!currentPassword || !newPassword || !confirmNewPassword || newPassword !== confirmNewPassword) {
            setError('Please fill in all password fields correctly.');
            return;
        }

        setUpdateLoading(prev => ({ ...prev, password: true }));
        setError('');
        setSuccess('');

        try {
            if (user) {
                const credential = EmailAuthProvider.credential(user.email, currentPassword);
                await reauthenticateWithCredential(user, credential);
                await updatePassword(user, newPassword);

                setNewPassword('');
                setConfirmNewPassword('');
                setCurrentPassword('');
                setSuccess('Password updated successfully.');
            }
        } catch (error) {
            setError('Failed to update password: ' + error.message);
        } finally {
            setUpdateLoading(prev => ({ ...prev, password: false }));
        }
    };

    if (loading || fetching) {
        return null;
    }

    if (!user) {
        return (
            <div className="container has-text-warning mt-5">
                <div className="has-text-centered">
                    <p>Please log in to access your profile.</p>
                </div>
            </div>
        );
    }

    return (
        <section className="hero is-dark-grey has-text-centered" style={{ paddingTop: 0, paddingBottom: 0 }}>
            <div className="hero-body" style={{ paddingTop: '1rem', paddingBottom: '1rem' }}>
                <div className="container">
                    <div className="columns is-centered">
                        <div className="column" style={{ marginTop: 0, marginBottom: 0 }}>
                            <div className="box lightning-border" style={{
                                width: '32vw',
                                maxHeight: '90vh',
                                margin: '0 auto',
                                padding: '2.5rem',
                                borderRadius: '12px',
                                background: 'linear-gradient(135deg, rgba(119, 44, 56, 0.95), rgba(119, 44, 56, 0.85))',
                                boxShadow: '0 10px 30px rgba(255, 215, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.18)',
                                overflow: 'auto',
                                boxSizing: 'border-box'
                            }}>
                                <div className="has-text-centered" style={{ marginBottom: '2.5rem' }}>
                                    <h1 className="title" style={{
                                        color: '#FFA500',
                                        letterSpacing: '0.05em',
                                        textShadow: '0 2px 8px rgba(255, 165, 0, 0.4)',
                                        marginBottom: '1.5rem',
                                        fontWeight: 700
                                    }}>
                                        Account Settings
                                    </h1>
                                </div>
                                
                                {/* Username Dropdown */}
                                <div className="profile-dropdown" style={{ marginBottom: '1.5rem', display: 'block' }}>
                                    <div 
                                        className="dropdown-header"
                                        onClick={() => toggleSection('username')}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            padding: '1rem 1.5rem',
                                            background: 'rgba(255, 255, 255, 0.05)',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            border: '1px solid rgba(255, 215, 0, 0.2)',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={e => {
                                            e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                                            e.target.style.borderColor = 'rgba(255, 215, 0, 0.4)';
                                        }}
                                        onMouseLeave={e => {
                                            e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                                            e.target.style.borderColor = 'rgba(255, 215, 0, 0.2)';
                                        }}
                                    >
                                        <h2 className="subtitle has-text-warning" style={{ margin: 0, fontWeight: 600 }}>
                                            Change Username
                                        </h2>
                                        <FontAwesomeIcon 
                                            icon={expandedSections.username ? faChevronUp : faChevronDown} 
                                            className="has-text-warning"
                                            style={{ transition: 'transform 0.3s ease' }}
                                        />
                                    </div>
                                    
                                    {expandedSections.username && (
                                        <div className="dropdown-content" style={{
                                            padding: '1.5rem',
                                            background: 'rgba(255, 255, 255, 0.08)',
                                            borderRadius: '0 0 8px 8px',
                                            border: '1px solid rgba(255, 215, 0, 0.3)',
                                            borderTop: 'none',
                                            animation: 'slideDown 0.3s ease',
                                            width: '100%',
                                            boxSizing: 'border-box'
                                        }}>
                                            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', width: '100%' }}>
                                                <div style={{ flex: '1 1 220px', minWidth: '220px' }}>
                                                    <label className="label has-text-warning">Current Username</label>
                                                    <div className="control">
                                                        <input
                                                            className="input"
                                                            type="text"
                                                            value={currentUsername}
                                                            disabled
                                                            style={{
                                                                border: '1px solid rgba(255, 215, 0, 0.3)',
                                                                borderRadius: '8px',
                                                                background: 'rgba(255,255,255,0.03)',
                                                                color: '#aaa',
                                                                cursor: 'not-allowed'
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ flex: '1 1 220px', minWidth: '220px', position: 'relative' }}>
                                                    <label htmlFor="newUsername" className="label has-text-warning">New Username</label>
                                                    <div className="control" style={{ position: 'relative' }}>
                                                        <input
                                                            className="input"
                                                            type="text"
                                                            id="newUsername"
                                                            name="newUsername"
                                                            value={newUsername}
                                                            onChange={e => setNewUsername(e.target.value)}
                                                            disabled={!isGold}
                                                            required={isGold}
                                                            style={{
                                                                border: '1px solid rgba(255, 215, 0, 0.3)',
                                                                borderRadius: '8px',
                                                                transition: 'all 0.3s ease',
                                                                background: isGold ? 'rgba(255, 255, 255, 0.05)' : 'rgba(255,255,255,0.03)',
                                                                backdropFilter: 'blur(10px)',
                                                                color: isGold ? '#FFD700' : '#aaa',
                                                                paddingLeft: isGold ? '1rem' : '2.2rem'
                                                            }}
                                                            onFocus={e => {
                                                                if (isGold) {
                                                                    e.target.style.borderColor = '#FFD700';
                                                                    e.target.style.boxShadow = '0 0 0 2px rgba(255, 215, 0, 0.2)';
                                                                }
                                                            }}
                                                            onBlur={e => {
                                                                e.target.style.borderColor = 'rgba(255, 215, 0, 0.3)';
                                                                e.target.style.boxShadow = 'none';
                                                            }}
                                                        />
                                                        {!isGold && (
                                                            <span style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', color: '#FFD700', pointerEvents: 'none' }}>
                                                                <FontAwesomeIcon icon={faLock} size="sm" />
                                                            </span>
                                                        )}
                                                    </div>
                                                    {!isGold && (
                                                        <div className="has-text-warning is-size-7" style={{ marginTop: '0.25rem', opacity: 0.8 }}>
                                                            Only Gold members can change their username.
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                            
                                            {/* Show confirm username only if new username is entered */}
                                            {newUsername && (
                                                <div style={{ marginTop: '1rem' }}>
                                                    <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', width: '100%' }}>
                                                        <div style={{ flex: '1 1 220px', minWidth: '220px' }}>
                                                            <label htmlFor="confirmUsername" className="label has-text-warning">Confirm New Username</label>
                                                            <div className="control">
                                                                <input
                                                                    className="input"
                                                                    type="text"
                                                                    id="confirmUsername"
                                                                    name="confirmUsername"
                                                                    value={confirmUsername}
                                                                    onChange={e => setConfirmUsername(e.target.value)}
                                                                    style={{
                                                                        border: '1px solid rgba(255, 215, 0, 0.3)',
                                                                        borderRadius: '8px',
                                                                        transition: 'all 0.3s ease',
                                                                        background: 'rgba(255, 255, 255, 0.05)',
                                                                        backdropFilter: 'blur(10px)'
                                                                    }}
                                                                    onFocus={e => {
                                                                        e.target.style.borderColor = '#FFD700';
                                                                        e.target.style.boxShadow = '0 0 0 2px rgba(255, 215, 0, 0.2)';
                                                                    }}
                                                                    onBlur={e => {
                                                                        e.target.style.borderColor = 'rgba(255, 215, 0, 0.3)';
                                                                        e.target.style.boxShadow = 'none';
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ flex: 1, minWidth: '220px' }}>
                                                            {/* Empty space to maintain layout */}
                                                        </div>
                                                    </div>
                                                    {validationErrors.username && (
                                                        <div className="has-text-danger is-size-7" style={{ marginTop: '0.25rem' }}>
                                                            {validationErrors.username}
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Update button for username */}
                                            {newUsername && confirmUsername && !validationErrors.username && (
                                                <div style={{ marginTop: '1.5rem' }}>
                                                    <button
                                                        onClick={handleUpdateUsername}
                                                        disabled={updateLoading.username}
                                                        className="button is-fullwidth"
                                                        style={{
                                                            padding: '0.75rem 1.5rem',
                                                            borderRadius: '8px',
                                                            fontWeight: '600',
                                                            letterSpacing: '0.5px',
                                                            fontSize: '1rem',
                                                            transition: 'all 0.2s cubic-bezier(.4,2,.3,1)',
                                                            border: '2px solid #FFA500',
                                                            background: updateLoading.username 
                                                                ? 'rgba(255, 165, 0, 0.5)' 
                                                                : 'linear-gradient(90deg, #FFA500 0%, #FFD700 100%)',
                                                            color: '#191919',
                                                            textTransform: 'uppercase',
                                                            boxShadow: '0 0 3px 0 #FFD70022',
                                                            cursor: updateLoading.username ? 'not-allowed' : 'pointer'
                                                        }}
                                                        onMouseEnter={e => {
                                                            if (!updateLoading.username) {
                                                                e.target.style.boxShadow = '0 0 6px 0 #FFD70022';
                                                                e.target.style.filter = 'brightness(1.03)';
                                                                e.target.style.transform = 'scale(1.005)';
                                                            }
                                                        }}
                                                        onMouseLeave={e => {
                                                            e.target.style.boxShadow = '0 0 3px 0 #FFD70022';
                                                            e.target.style.filter = 'none';
                                                            e.target.style.transform = 'scale(1)';
                                                        }}
                                                    >
                                                        {updateLoading.username ? 'Updating...' : 'Update Username'}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Email Dropdown */}
                                <div className="profile-dropdown" style={{ marginBottom: '1.5rem', display: 'block' }}>
                                    <div 
                                        className="dropdown-header"
                                        onClick={() => toggleSection('email')}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            padding: '1rem 1.5rem',
                                            background: 'rgba(255, 255, 255, 0.05)',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            border: '1px solid rgba(255, 215, 0, 0.2)',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={e => {
                                            e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                                            e.target.style.borderColor = 'rgba(255, 215, 0, 0.4)';
                                        }}
                                        onMouseLeave={e => {
                                            e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                                            e.target.style.borderColor = 'rgba(255, 215, 0, 0.2)';
                                        }}
                                    >
                                        <h2 className="subtitle has-text-warning" style={{ margin: 0, fontWeight: 600 }}>
                                            Change Email
                                        </h2>
                                        <FontAwesomeIcon 
                                            icon={expandedSections.email ? faChevronUp : faChevronDown} 
                                            className="has-text-warning"
                                            style={{ transition: 'transform 0.3s ease' }}
                                        />
                                    </div>
                                    
                                    {expandedSections.email && (
                                        <div className="dropdown-content" style={{
                                            padding: '1.5rem',
                                            background: 'rgba(255, 255, 255, 0.08)',
                                            borderRadius: '0 0 8px 8px',
                                            border: '1px solid rgba(255, 215, 0, 0.3)',
                                            borderTop: 'none',
                                            animation: 'slideDown 0.3s ease',
                                            width: '100%',
                                            boxSizing: 'border-box'
                                        }}>
                                            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', width: '100%' }}>
                                                <div style={{ flex: '1 1 220px', minWidth: '220px' }}>
                                                    <label className="label has-text-warning">Current Email</label>
                                                    <div className="control">
                                                        <input
                                                            className="input"
                                                            type="email"
                                                            value={currentEmail}
                                                            disabled
                                                            style={{
                                                                border: '1px solid rgba(255, 215, 0, 0.3)',
                                                                borderRadius: '8px',
                                                                background: 'rgba(255,255,255,0.03)',
                                                                color: '#aaa',
                                                                cursor: 'not-allowed'
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ flex: '1 1 220px', minWidth: '220px' }}>
                                                    <label htmlFor="newEmail" className="label has-text-warning">New Email</label>
                                                    <div className="control">
                                                        <input
                                                            className="input has-text-warning"
                                                            type="email"
                                                            id="newEmail"
                                                            name="newEmail"
                                                            value={newEmail}
                                                            onChange={e => setNewEmail(e.target.value)}
                                                            required
                                                            style={{
                                                                border: '1px solid rgba(255, 215, 0, 0.3)',
                                                                borderRadius: '8px',
                                                                transition: 'all 0.3s ease',
                                                                background: 'rgba(255, 255, 255, 0.05)',
                                                                backdropFilter: 'blur(10px)'
                                                            }}
                                                            onFocus={e => {
                                                                e.target.style.borderColor = '#FFD700';
                                                                e.target.style.boxShadow = '0 0 0 2px rgba(255, 215, 0, 0.2)';
                                                            }}
                                                            onBlur={e => {
                                                                e.target.style.borderColor = 'rgba(255, 215, 0, 0.3)';
                                                                e.target.style.boxShadow = 'none';
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Show confirm email only if new email is entered */}
                                            {newEmail && (
                                                <div style={{ marginTop: '1rem' }}>
                                                    <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', width: '100%' }}>
                                                        <div style={{ flex: '1 1 220px', minWidth: '220px' }}>
                                                            <label htmlFor="confirmEmail" className="label has-text-warning">Confirm New Email</label>
                                                            <div className="control">
                                                                <input
                                                                    className="input"
                                                                    type="email"
                                                                    id="confirmEmail"
                                                                    name="confirmEmail"
                                                                    value={confirmEmail}
                                                                    onChange={e => setConfirmEmail(e.target.value)}
                                                                    style={{
                                                                        border: '1px solid rgba(255, 215, 0, 0.3)',
                                                                        borderRadius: '8px',
                                                                        transition: 'all 0.3s ease',
                                                                        background: 'rgba(255, 255, 255, 0.05)',
                                                                        backdropFilter: 'blur(10px)'
                                                                    }}
                                                                    onFocus={e => {
                                                                        e.target.style.borderColor = '#FFD700';
                                                                        e.target.style.boxShadow = '0 0 0 2px rgba(255, 215, 0, 0.2)';
                                                                    }}
                                                                    onBlur={e => {
                                                                        e.target.style.borderColor = 'rgba(255, 215, 0, 0.3)';
                                                                        e.target.style.boxShadow = 'none';
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ flex: 1, minWidth: '220px' }}>
                                                            {/* Empty space to maintain layout */}
                                                        </div>
                                                    </div>
                                                    {validationErrors.email && (
                                                        <div className="has-text-danger is-size-7" style={{ marginTop: '0.25rem' }}>
                                                            {validationErrors.email}
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Update button for email */}
                                            {newEmail && confirmEmail && !validationErrors.email && (
                                                <div style={{ marginTop: '1.5rem' }}>
                                                    <button
                                                        onClick={handleUpdateEmail}
                                                        disabled={updateLoading.email}
                                                        className="button is-fullwidth"
                                                        style={{
                                                            padding: '0.75rem 1.5rem',
                                                            borderRadius: '8px',
                                                            fontWeight: '600',
                                                            letterSpacing: '0.5px',
                                                            fontSize: '1rem',
                                                            transition: 'all 0.2s cubic-bezier(.4,2,.3,1)',
                                                            border: '2px solid #FFA500',
                                                            background: updateLoading.email 
                                                                ? 'rgba(255, 165, 0, 0.5)' 
                                                                : 'linear-gradient(90deg, #FFA500 0%, #FFD700 100%)',
                                                            color: '#191919',
                                                            textTransform: 'uppercase',
                                                            boxShadow: '0 0 3px 0 #FFD70022',
                                                            cursor: updateLoading.email ? 'not-allowed' : 'pointer'
                                                        }}
                                                        onMouseEnter={e => {
                                                            if (!updateLoading.email) {
                                                                e.target.style.boxShadow = '0 0 6px 0 #FFD70022';
                                                                e.target.style.filter = 'brightness(1.03)';
                                                                e.target.style.transform = 'scale(1.005)';
                                                            }
                                                        }}
                                                        onMouseLeave={e => {
                                                            e.target.style.boxShadow = '0 0 3px 0 #FFD70022';
                                                            e.target.style.filter = 'none';
                                                            e.target.style.transform = 'scale(1)';
                                                        }}
                                                    >
                                                        {updateLoading.email ? 'Updating...' : 'Update Email'}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {/* Password Dropdown */}
                                <div className="profile-dropdown" style={{ marginBottom: '2rem', display: 'block' }}>
                                    <div 
                                        className="dropdown-header"
                                        onClick={() => toggleSection('password')}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            padding: '1rem 1.5rem',
                                            background: 'rgba(255, 255, 255, 0.05)',
                                            borderRadius: '8px',
                                            cursor: 'pointer',
                                            border: '1px solid rgba(255, 215, 0, 0.2)',
                                            transition: 'all 0.3s ease'
                                        }}
                                        onMouseEnter={e => {
                                            e.target.style.background = 'rgba(255, 255, 255, 0.08)';
                                            e.target.style.borderColor = 'rgba(255, 215, 0, 0.4)';
                                        }}
                                        onMouseLeave={e => {
                                            e.target.style.background = 'rgba(255, 255, 255, 0.05)';
                                            e.target.style.borderColor = 'rgba(255, 215, 0, 0.2)';
                                        }}
                                    >
                                        <h2 className="subtitle has-text-warning" style={{ margin: 0, fontWeight: 600 }}>
                                            Change Password
                                        </h2>
                                        <FontAwesomeIcon 
                                            icon={expandedSections.password ? faChevronUp : faChevronDown} 
                                            className="has-text-warning"
                                            style={{ transition: 'transform 0.3s ease' }}
                                        />
                                    </div>
                                    
                                    {expandedSections.password && (
                                        <div className="dropdown-content" style={{
                                            padding: '1.5rem',
                                            background: 'rgba(255, 255, 255, 0.08)',
                                            borderRadius: '0 0 8px 8px',
                                            border: '1px solid rgba(255, 215, 0, 0.3)',
                                            borderTop: 'none',
                                            animation: 'slideDown 0.3s ease',
                                            width: '100%',
                                            boxSizing: 'border-box'
                                        }}>
                                            <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', width: '100%' }}>
                                                <div style={{ flex: '1 1 220px', minWidth: '220px' }}>
                                                    <label htmlFor="currentPassword" className="label has-text-warning">Current Password</label>
                                                    <div className="control">
                                                        <input
                                                            className="input"
                                                            type="password"
                                                            id="currentPassword"
                                                            name="currentPassword"
                                                            value={currentPassword}
                                                            onChange={e => setCurrentPassword(e.target.value)}
                                                            style={{
                                                                border: '1px solid rgba(255, 215, 0, 0.3)',
                                                                borderRadius: '8px',
                                                                transition: 'all 0.3s ease',
                                                                background: 'rgba(255, 255, 255, 0.05)',
                                                                backdropFilter: 'blur(10px)'
                                                            }}
                                                            onFocus={e => {
                                                                e.target.style.borderColor = '#FFD700';
                                                                e.target.style.boxShadow = '0 0 0 2px rgba(255, 215, 0, 0.2)';
                                                            }}
                                                            onBlur={e => {
                                                                e.target.style.borderColor = 'rgba(255, 215, 0, 0.3)';
                                                                e.target.style.boxShadow = 'none';
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                                <div style={{ flex: '1 1 220px', minWidth: '220px' }}>
                                                    <label htmlFor="newPassword" className="label has-text-warning">New Password</label>
                                                    <div className="control">
                                                        <input
                                                            className="input"
                                                            type="password"
                                                            id="newPassword"
                                                            name="newPassword"
                                                            value={newPassword}
                                                            onChange={e => setNewPassword(e.target.value)}
                                                            style={{
                                                                border: '1px solid rgba(255, 215, 0, 0.3)',
                                                                borderRadius: '8px',
                                                                transition: 'all 0.3s ease',
                                                                background: 'rgba(255, 255, 255, 0.05)',
                                                                backdropFilter: 'blur(10px)'
                                                            }}
                                                            onFocus={e => {
                                                                e.target.style.borderColor = '#FFD700';
                                                                e.target.style.boxShadow = '0 0 0 2px rgba(255, 215, 0, 0.2)';
                                                            }}
                                                            onBlur={e => {
                                                                e.target.style.borderColor = 'rgba(255, 215, 0, 0.3)';
                                                                e.target.style.boxShadow = 'none';
                                                            }}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Show confirm password only if new password is entered */}
                                            {newPassword && (
                                                <div style={{ marginTop: '1rem' }}>
                                                    <div style={{ display: 'flex', gap: '1.5rem', flexWrap: 'wrap', width: '100%' }}>
                                                        <div style={{ flex: '1 1 220px', minWidth: '220px' }}>
                                                            <label htmlFor="confirmNewPassword" className="label has-text-warning">Confirm New Password</label>
                                                            <div className="control">
                                                                <input
                                                                    className="input"
                                                                    type="password"
                                                                    id="confirmNewPassword"
                                                                    name="confirmNewPassword"
                                                                    value={confirmNewPassword}
                                                                    onChange={e => setConfirmNewPassword(e.target.value)}
                                                                    style={{
                                                                        border: '1px solid rgba(255, 215, 0, 0.3)',
                                                                        borderRadius: '8px',
                                                                        transition: 'all 0.3s ease',
                                                                        background: 'rgba(255, 255, 255, 0.05)',
                                                                        backdropFilter: 'blur(10px)'
                                                                    }}
                                                                    onFocus={e => {
                                                                        e.target.style.borderColor = '#FFD700';
                                                                        e.target.style.boxShadow = '0 0 0 2px rgba(255, 215, 0, 0.2)';
                                                                    }}
                                                                    onBlur={e => {
                                                                        e.target.style.borderColor = 'rgba(255, 215, 0, 0.3)';
                                                                        e.target.style.boxShadow = 'none';
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div style={{ flex: 1, minWidth: '220px' }}>
                                                            {/* Empty space to maintain layout */}
                                                        </div>
                                                    </div>
                                                    {validationErrors.password && (
                                                        <div className="has-text-danger is-size-7" style={{ marginTop: '0.25rem' }}>
                                                            {validationErrors.password}
                                                        </div>
                                                    )}
                                                </div>
                                            )}

                                            {/* Update button for password */}
                                            {currentPassword && newPassword && confirmNewPassword && !validationErrors.password && (
                                                <div style={{ marginTop: '1.5rem' }}>
                                                    <button
                                                        onClick={handleUpdatePassword}
                                                        disabled={updateLoading.password}
                                                        className="button is-fullwidth"
                                                        style={{
                                                            padding: '0.75rem 1.5rem',
                                                            borderRadius: '8px',
                                                            fontWeight: '600',
                                                            letterSpacing: '0.5px',
                                                            fontSize: '1rem',
                                                            transition: 'all 0.2s cubic-bezier(.4,2,.3,1)',
                                                            border: '2px solid #FFA500',
                                                            background: updateLoading.password 
                                                                ? 'rgba(255, 165, 0, 0.5)' 
                                                                : 'linear-gradient(90deg, #FFA500 0%, #FFD700 100%)',
                                                            color: '#191919',
                                                            textTransform: 'uppercase',
                                                            boxShadow: '0 0 3px 0 #FFD70022',
                                                            cursor: updateLoading.password ? 'not-allowed' : 'pointer'
                                                        }}
                                                        onMouseEnter={e => {
                                                            if (!updateLoading.password) {
                                                                e.target.style.boxShadow = '0 0 6px 0 #FFD70022';
                                                                e.target.style.filter = 'brightness(1.03)';
                                                                e.target.style.transform = 'scale(1.005)';
                                                            }
                                                        }}
                                                        onMouseLeave={e => {
                                                            e.target.style.boxShadow = '0 0 3px 0 #FFD70022';
                                                            e.target.style.filter = 'none';
                                                            e.target.style.transform = 'scale(1)';
                                                        }}
                                                    >
                                                        {updateLoading.password ? 'Updating...' : 'Update Password'}
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                {error && (
                                    <div className="notification is-danger mb-4" style={{
                                        borderRadius: '8px',
                                        border: '1px solid rgba(255, 0, 0, 0.3)'
                                    }}>
                                        <p className="subtitle">{error}</p>
                                    </div>
                                )}
                                {success && (
                                    <div className="notification is-success mb-4" style={{
                                        borderRadius: '8px',
                                        border: '1px solid rgba(0, 255, 0, 0.3)'
                                    }}>
                                        <p className="subtitle">{success}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default ProfileComponent;
